// Typed fetch client for the Worker API. Cookies (session) are sent automatically.
// One shared world: no garden ids, no kid ids — identity comes from the session.

import type {
  AdminPlayerRow,
  CatchFishResult,
  CompleteLessonResult,
  DungeonLootResult,
  FarmActionResult,
  LeaderboardRow,
  SpeechCheckResult,
  WorldPlot,
  WorldSnapshot,
} from "../shared/types";

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    let message = res.statusText;
    try {
      message = ((await res.json()) as { error?: string }).error ?? message;
    } catch {
      /* ignore */
    }
    const err = new Error(message) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return (await res.json()) as T;
}

export interface MeResponse {
  email: string;
  /** Empty string until child onboarding is done. */
  playerId: string;
  needsOnboarding: boolean;
  isAdmin: boolean;
}

export interface PendingInvite {
  email: string;
  invitedAt: number;
}

export interface PendingJoinRequest {
  id: string;
  email: string;
  subject?: string;
  body?: string;
  requestedAt: number;
}

export const api = {
  // ---- auth (OTP: request a 6-digit code, then exchange it) ----
  requestCode: (email: string) =>
    req<{ ok: true }>("/api/auth/request", { method: "POST", body: JSON.stringify({ email }) }),

  exchangeCode: (email: string, code: string) =>
    req<{ ok: true }>("/api/auth/exchange", { method: "POST", body: JSON.stringify({ email, code }) }),

  // Preview-only: sign in as the admin with one tap (server 404s in production).
  previewLogin: () => req<{ ok: true }>("/api/auth/preview-login", { method: "POST" }),

  me: () => req<MeResponse>("/api/auth/me"),

  logout: () => req<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  // ---- onboarding (one child per account) ----
  createPlayer: (childName: string, character: string) =>
    req<{ ok: true; playerId: string }>("/api/player", {
      method: "POST",
      body: JSON.stringify({ childName, character }),
    }),

  renamePlayer: (childName: string) =>
    req<{ ok: true }>("/api/player/rename", {
      method: "POST",
      body: JSON.stringify({ childName }),
    }),

  changeCharacter: (character: string) =>
    req<{ ok: true }>("/api/player/character", {
      method: "POST",
      body: JSON.stringify({ character }),
    }),

  // ---- world ----
  getGarden: () => req<WorldSnapshot>("/api/garden"),

  completeLesson: (nodeId: string, score: number) =>
    req<CompleteLessonResult>("/api/lesson/complete", {
      method: "POST",
      body: JSON.stringify({ nodeId, score }),
    }),

  /** Grade one spoken phonics attempt (audio is transcribed server-side and dropped). */
  checkSpeech: (nodeId: string, itemIndex: number, audioB64: string, mimeType: string) =>
    req<SpeechCheckResult>("/api/speech/check", {
      method: "POST",
      body: JSON.stringify({ nodeId, itemIndex, audioB64, mimeType }),
    }),

  plant: (plotIndex: number, species: string) =>
    req<WorldPlot>("/api/garden/plant", {
      method: "POST",
      body: JSON.stringify({ plotIndex, species }),
    }),

  water: (plotIndex: number) =>
    req<WorldPlot>("/api/garden/water", {
      method: "POST",
      body: JSON.stringify({ plotIndex }),
    }),

  harvest: (plotIndex: number) =>
    req<FarmActionResult>("/api/garden/harvest", {
      method: "POST",
      body: JSON.stringify({ plotIndex }),
    }),

  buySeed: (species: string) =>
    req<FarmActionResult>("/api/shop/buy", {
      method: "POST",
      body: JSON.stringify({ species }),
    }),

  chop: (treeId: string) =>
    req<FarmActionResult>("/api/world/chop", {
      method: "POST",
      body: JSON.stringify({ treeId }),
    }),

  collectDungeon: (gems: number) =>
    req<DungeonLootResult>("/api/dungeon/collect", {
      method: "POST",
      body: JSON.stringify({ gems }),
    }),

  catchFish: (species: string) =>
    req<CatchFishResult>("/api/fishing/catch", {
      method: "POST",
      body: JSON.stringify({ species }),
    }),

  leaderboard: () => req<{ rows: LeaderboardRow[] }>("/api/leaderboard"),

  /** ICE config for the voice-chat WebRTC mesh (STUN-only when TURN isn't configured). */
  iceServers: () => req<{ iceServers: RTCIceServer[]; ttlSeconds: number }>("/api/rtc/ice"),

  // ---- admin panel (server-gated by ADMIN_EMAIL) ----
  admin: {
    players: () => req<{ players: AdminPlayerRow[] }>("/api/admin/players"),

    invites: () => req<{ invites: PendingInvite[] }>("/api/admin/invites"),

    invite: (email: string) =>
      req<{ ok: true }>("/api/admin/invite", { method: "POST", body: JSON.stringify({ email }) }),

    revoke: (email: string) =>
      req<{ ok: true }>("/api/admin/revoke", { method: "POST", body: JSON.stringify({ email }) }),

    compact: () =>
      req<{ ok: true; berths: number; reclaimed: number }>("/api/admin/compact", { method: "POST" }),

    joinRequests: () => req<{ requests: PendingJoinRequest[] }>("/api/admin/join-requests"),

    dismissJoinRequest: (email: string) =>
      req<{ ok: true }>("/api/admin/join-requests/dismiss", {
        method: "POST",
        body: JSON.stringify({ email }),
      }),
  },

  // ---- push ----
  vapidPublicKey: () => req<{ key: string }>("/api/push/vapid-public"),

  subscribePush: (subscription: PushSubscriptionJSON) =>
    req<{ ok: true; id: string }>("/api/push/subscribe", {
      method: "POST",
      body: JSON.stringify({ subscription }),
    }),
};
